import {createContext} from 'preact';
import {useEffect, useState, useCallback} from 'preact/hooks';
import {XMarkIcon} from "@heroicons/react/24/solid";

export const NotificationContext = createContext();

export function NotificationProvider({children}) {
    const [notifications, setNotifications] = useState([]);

    const addNotification = useCallback((message, type = 'info') => {
        const id = Date.now() + Math.random();
        setNotifications(n => [...n, {id, message, type}]);
        setTimeout(() => {
            setNotifications(n => n.filter(notif => notif.id !== id));
        }, 5000);
    }, []);

    const removeNotification = useCallback((id) => {
        setNotifications(n => n.filter(notif => notif.id !== id));
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('info')) {
            addNotification(params.get('info'), 'info');
        }
        if (params.get('error')) {
            addNotification(params.get('error'), 'error');
        }
        if (params.get('warning')) {
            addNotification(params.get('warning'), 'warning');
        }
    }, [addNotification]);

    return (
        <NotificationContext.Provider value={{addNotification}}>
            {children}
            <Notifications notifications={notifications} onClose={removeNotification}/>
        </NotificationContext.Provider>
    );
}

export function Notifications({notifications, onClose}) {
    const [visibleIds, setVisibleIds] = useState([]);

    useEffect(() => {
        setVisibleIds(notifications.map(n => n.id));
    }, [notifications]);

    const handleClose = (id) => {
        setVisibleIds(ids => ids.filter(vid => vid !== id));
        setTimeout(() => onClose(id), 300); // Wait for animation
    };

    const typeClasses = {
        info: "text-blue-800 bg-blue-50",
        success: "text-green-800 bg-green-50",
        error: "text-red-800 bg-red-50",
        warning: "text-yellow-800 bg-yellow-50"
    };

    return (
        <div className="fixed left-1/2 top-8 -translate-x-1/2 z-50 flex flex-col gap-2 items-center w-100 w-[90%] md:max-w-[35%]">
            {notifications.map(({id, message, type}) => {
                const isVisible = visibleIds.includes(id);
                return (
                    <div
                        key={id}
                        className={`py-3 px-6 text-sm rounded-lg min-w-[200px] shadow flex justify-between items-center transition-all duration-300
                        ${typeClasses[type] || typeClasses.info}
                        ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
                    >
                        <span className="font-medium">{message}</span>
                        <button
                            type="button"
                            onClick={() => handleClose(id)}
                            aria-label="Dismiss notification"
                            className="cursor-pointer ml-3"
                            style="margin-right: -10px"
                        >
                            <XMarkIcon className="w-5 h-5" aria-hidden="true"/>
                        </button>
                    </div>
                );
            })}
        </div>
    );
}
